import { createElement, type ReactElement } from 'react';
import { Outlet, type RouteObject } from 'react-router';

import {
  type RouteBreadcrumbModel,
  RouteEnum,
  type RouteModel,
} from '@/layouts/routes/Route.model.ts';
import { ROUTES } from '@/layouts/routes/routes';
import { PageLoading } from '@/routes/PageLoading.comp.tsx';
import { RoutedView } from '@/routes/RoutedView.comp.tsx';
import { RouteErrorView } from '@/routes/RouteErrorView.comp.tsx';
import { createRouteGuardLoader } from '@/routes/routeGuard.loader.ts';
import { NotFound } from '@/views/generalError/notAuthorized/NotFound.comp';

export type BareLayoutVariant = 'assessment' | 'login';

interface CreateAppRoutesOptions {
  appLayout: ReactElement;
  bareLayout: ReactElement;
  rootLayout: ReactElement;
  routes?: RouteModel[];
}

interface RouteHandle {
  bareVariant?: BareLayoutVariant;
  breadcrumb?: RouteBreadcrumbModel;
  path?: RouteEnum;
}

const BARE_LAYOUT_VARIANTS: Partial<Record<RouteEnum, BareLayoutVariant>> = {
  [RouteEnum.ASSESSMENT_EDIT]: 'assessment',
  [RouteEnum.ASSESSMENT_ID]: 'assessment',
  [RouteEnum.LOGIN]: 'login',
};

const ASSESSMENTS_WILDCARD_PATH = `${RouteEnum.ASSESSMENTS}/*`;

const buildLeafHandle = (
  path: RouteEnum,
  breadcrumb: RouteBreadcrumbModel | undefined,
): RouteHandle | undefined => {
  const bareVariant = getBareVariant(path);

  if (!breadcrumb && !bareVariant) {
    return undefined;
  }

  return {
    ...(breadcrumb ? { breadcrumb } : {}),
    ...(bareVariant ? { bareVariant } : {}),
    path,
  };
};

const createLayoutRoute = (element: ReactElement, children: RouteObject[]): RouteObject => ({
  children: [{ children, errorElement: createElement(RouteErrorView) }],
  element,
});

const getBareVariant = (path: RouteEnum): BareLayoutVariant | undefined =>
  BARE_LAYOUT_VARIANTS[path];

const isBareRoute = (path: RouteEnum): boolean => getBareVariant(path) !== undefined;

const resolveBareVariant = (
  matches: ReadonlyArray<{ handle: unknown }>,
): BareLayoutVariant | undefined => {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle as RouteHandle | undefined;
    if (handle?.bareVariant) {
      return handle.bareVariant;
    }
  }
  return undefined;
};

const toRouteBranch = ({ breadcrumb, loadView, path }: RouteModel): RouteObject => ({
  children: [
    {
      handle: buildLeafHandle(path, breadcrumb),
      lazy: async () => {
        const { default: component } = await loadView();
        return { element: createElement(RoutedView, { component }) };
      },
      loader: createRouteGuardLoader(path),
      path,
    },
  ],
  element: createElement(Outlet),
});

const createAppRoutes = ({
  appLayout,
  bareLayout,
  rootLayout,
  routes = ROUTES,
}: CreateAppRoutesOptions): RouteObject[] => {
  const appRoutes = routes.filter(route => !isBareRoute(route.path));
  const bareRoutes = routes.filter(route => isBareRoute(route.path));
  const hasAssessmentBareRoutes = bareRoutes.some(
    ({ path }) => path === RouteEnum.ASSESSMENT_EDIT || path === RouteEnum.ASSESSMENT_ID,
  );
  const bareCatchAlls: RouteObject[] = hasAssessmentBareRoutes
    ? [
        {
          element: createElement(NotFound),
          handle: { bareVariant: 'assessment' as const },
          path: ASSESSMENTS_WILDCARD_PATH,
        },
      ]
    : [];
  const appChildren = [
    ...appRoutes.map(toRouteBranch),
    { element: createElement(NotFound), path: '*' },
  ];
  const bareChildren = [...bareRoutes.map(toRouteBranch), ...bareCatchAlls];

  return [
    {
      children: [
        createLayoutRoute(appLayout, appChildren),
        createLayoutRoute(bareLayout, bareChildren),
      ],
      element: rootLayout,
      hydrateFallbackElement: createElement(PageLoading),
    },
  ];
};

export const routesUtil = {
  buildLeafHandle,
  createAppRoutes,
  getBareVariant,
  isBareRoute,
  resolveBareVariant,
  toRouteBranch,
};
